import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { PreviewTable } from '../../../components/PreviewTable';
import { Tooltip } from '../../../components/Tooltip';
import { Select } from '../../../components/inputs/Select';
import { InlineAddTable, InlineRow } from '../../../components/inline-add-table/InlineAddTable';
import { TdSelect } from '../../../components/inline-add-table/cells';
import { ApiError } from '../../../lib/apiError';
import { focusValidationError } from '../../../lib/validationFocus';
import { usersApi } from '../../administration/users/usersApi';
import type { UserListItem } from '../../administration/users/userTypes';
import { approvalRoutingApi } from '../approvalsApi';
import { sampleRouting } from '../utils/sampleRouting';
import {
  APPROVAL_DECISION,
  APPROVAL_DECISION_OPTIONS,
  APPROVAL_MODE,
  APPROVAL_MODE_OPTIONS,
} from '../approvalTypes';
import type {
  ApprovalApproverInput,
  ApprovalDecisionType,
  ApprovalMode,
  ApprovalWorkflowOption,
} from '../approvalTypes';

const ID_PREFIX = 'approval-routing';

/**
 * Configures one workflow's approver chain.
 *
 * **ONE request for the whole chain, and it REPLACES the set.** An approver removed here is
 * deactivated in the same transaction that writes the rest, which is what makes Cancel mean
 * something and lets Remove be a row leaving a table rather than an immediate revocation — the
 * shape the repository's share dialog already takes.
 */
export function ApprovalRoutingModal({
  open,
  editingId,
  workflows,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null configures a workflow for the first time. */
  editingId: string | null;
  workflows: ApprovalWorkflowOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [workflowKey, setWorkflowKey] = useState<string | null>(null);
  const [rows, setRows] = useState<ApprovalApproverInput[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editingId !== null);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    THE ACCOUNT PICKER FAILS SOFT. It reads the users module, and a reader who may configure
    routing is not thereby a reader of that module — so a failed load leaves the picker empty
    and the rest of the form working, rather than failing the whole dialog. The same call the
    share dialog's own people-picker makes.
  */
  const [accounts, setAccounts] = useState<UserListItem[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    usersApi
      .list({ page: 1, pageSize: 100, status: 'active' }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setAccounts(page.items);
      })
      .catch(() => {
        // Deliberately swallowed — see above.
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (editingId === null) return;

    const controller = new AbortController();

    approvalRoutingApi
      .get(editingId, controller.signal)
      .then((detail) => {
        if (controller.signal.aborted) return;

        setWorkflowKey(detail.workflowKey);
        setRows(
          detail.approvers.map((a) => ({
            userId: a.userId,
            mode: a.mode,
            decisionType: a.decisionType,
          })),
        );
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load this routing.', 0),
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [editingId]);

  const update = useCallback((index: number, patch: Partial<ApprovalApproverInput>) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }, []);

  const filled = rows.filter((r) => r.userId.trim().length > 0);

  const duplicate = filled.some(
    (row, i) => filled.findIndex((other) => other.userId === row.userId) !== i,
  );

  const valid =
    (workflowKey?.trim().length ?? 0) > 0 && filled.length > 0 && filled.length === rows.length && !duplicate;

  const canSave = valid && confirmed && !saving && !loading;

  /*
    WHY SAVE IS DISABLED, naming the FIRST thing missing — "every disabled control says why",
    and this form had nothing. It did not show while a red alert sat under the table explaining
    the commonest case; the grid's auto-inserted blank row is now the resting state, so that
    alert correctly waits and the button is what has to speak.
  */
  const saveDisabledReason = saving || loading
    ? null
    : (workflowKey?.trim().length ?? 0) === 0
      ? 'Choose the workflow this routing configures.'
      : filled.length === 0
        ? 'Choose an approver for the first row.'
        : filled.length !== rows.length
          ? 'Every row needs an approver. Remove the empty one, or choose an account for it.'
          : duplicate
            ? 'One account is listed twice. Each approver signs once.'
            : !confirmed
              ? 'Tick the confirm box to save.'
              : null;

  const workflow = workflows.find((w) => w.key === workflowKey) ?? null;

  /*
    EVERY REQUIRED FIELD, so the primary action enables on the first try — and both of them are
    foreign keys, so both are resolved against the catalogs this form has already loaded.
  */
  function generate() {
    const next = sampleRouting({ workflows, accounts, lockedWorkflowKey: editingId === null ? null : workflowKey });

    if (editingId === null) setWorkflowKey(next.workflowKey);
    setRows(next.approvers);
  }

  async function save() {
    if (!canSave || !workflowKey) return;

    setSaving(true);
    setError(null);

    try {
      await approvalRoutingApi.save({ workflowKey, approvers: rows });
      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this routing.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  /*
    NO Autogenerate. Every field here names a real account and a real routing decision — a
    generated chain would hand sign-off authority to whoever the generator happened to pick,
    which is the one place a dev affordance would be actively dangerous rather than merely
    unhelpful. The ceiling-form's `Match proposed` takes the same call.
  */
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={workflow ? `Approvers for ${workflow.name}` : 'Configure workflow approvers'}
      size="3xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving || loading }}
      confirm={{
        id: `${ID_PREFIX}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          THE CONSEQUENCE BEFORE THE TICK, and it is the opposite of what this form used to
          promise. Saving does NOT only change where the next submission routes — it re-routes
          the requests already waiting, which is what an administrator naming themselves the
          approver expects and did not get. A reader who thought this was forward-looking would
          otherwise find decisions arriving in their queue with nothing having said so.

          It states what is PRESERVED as well, because that is the half somebody about to edit
          a live chain is actually worried about.
        */}
        <p className="text-xs text-slate-600">
          This applies to requests that are already waiting, not just to the next one. Approvers
          added here are asked straight away; approvers removed are no longer asked. Signatures
          already given are kept, and nobody is asked to sign twice.
        </p>

        {editingId === null ? (
          <Select
            id={`${ID_PREFIX}-workflow-key`}
            label="Workflow"
            required
            description="Only workflows a module actually submits are listed — each one here has code behind it that can route a record and act on the decision."
            value={workflowKey}
            onChange={setWorkflowKey}
            options={workflows.map((w) => ({
              value: w.key,
              label: w.name,
              sublabel: w.isConfigured ? `${w.description} · already configured` : w.description,
            }))}
            error={
              workflowKey ? error?.fieldError('workflowKey') : 'Choose the workflow to configure.'
            }
            disabled={saving}
          />
        ) : (
          /*
            STATED, not offered, on an edit. Moving a chain to a different workflow is a
            retirement and a fresh configuration, not an edit — a picker here would let one
            click re-point who signs off on something else entirely.
          */
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Workflow', value: workflow?.name ?? workflowKey },
              { label: 'Signs off on', value: workflow?.description ?? null },
            ]}
          />
        )}

        <InlineAddTable
          headers={['Approver *', 'Activation', 'Signs']}
          /*
            NO `flexColumn`, and that is a measurement rather than a preference: the flexing
            branch of <InlineAddTable> asks its chosen column for `width: 100%` under a table
            carrying `min-width: max-content`, which is circular — Chromium resolves it at
            999,512px, so every column after the flex one is pushed off the scroll region. The
            declared-width branch keeps each column exactly as declared, which is what
            <IssuanceModal> already uses. Sized to fill the 3xl modal.
          */
          colWidths={[560, 200, 200]}
          resizeKey={`${ID_PREFIX}-approvers-v1`}
          rowCount={rows.length}
          onAddRow={() =>
            setRows((current) => [
              ...current,
              // Sequential and Approve are the ordinary chain: one at a time, authorizing.
              { userId: '', mode: APPROVAL_MODE.sequential, decisionType: APPROVAL_DECISION.approve },
            ])
          }
          addDisabled={saving}
          addButtonId={`${ID_PREFIX}-add-approver`}
          /*
            SHORT, because the footer sits INSIDE the grid's own horizontal scroll region and a
            paragraph there is clipped at the table's right edge. The full explanation is in the
            body above, where it has the modal's whole width.
          */
          footerContent={
            <p className="text-xs text-slate-600">One row per approver. Every one of them signs.</p>
          }
        >
          {rows.map((row, index) => (
            /*
              <InlineRow>, never a hand-rolled <tr>. The component draws the `#` gutter cell and
              the trailing remove cell, so a row that writes its own data cells alone lands one
              column LEFT of every header — which is the defect this grid shipped with: the
              activation value sat under the APPROVER heading and the remove icon under SIGNS.
            */
            <InlineRow
              key={index}
              idx={index + 1}
              onRemove={() => setRows((current) => current.filter((_, i) => i !== index))}
            >
              <TdSelect
                id={`${ID_PREFIX}-approvers.${index}.userId`}
                ariaLabel={`Approver ${index + 1}`}
                value={row.userId || null}
                onChange={(next) => update(index, { userId: next })}
                options={accounts.map((a) => ({
                  value: a.id,
                  label: a.displayName,
                  sublabel: a.roleName ?? a.email,
                }))}
                remote
                disabled={saving}
              />

              <TdSelect
                id={`${ID_PREFIX}-approvers.${index}.mode`}
                ariaLabel={`Activation for approver ${index + 1}`}
                // `String(value)` out, `Number(next)` back: the wire is numeric and <Select>
                // binds strings, which is the convention the issuance form's method picker set.
                value={String(row.mode)}
                onChange={(next) => update(index, { mode: Number(next) as ApprovalMode })}
                options={APPROVAL_MODE_OPTIONS}
                sort="none"
                disabled={saving}
              />

              <TdSelect
                id={`${ID_PREFIX}-approvers.${index}.decisionType`}
                ariaLabel={`What approver ${index + 1} signs`}
                value={String(row.decisionType)}
                onChange={(next) =>
                  update(index, { decisionType: Number(next) as ApprovalDecisionType })
                }
                options={APPROVAL_DECISION_OPTIONS}
                sort="none"
                disabled={saving}
              />
            </InlineRow>
          ))}
        </InlineAddTable>

        {/*
          THE CLIENT-SIDE REFUSALS, stated where the reader can act on them. Both are also
          enforced server-side and named per row there — these exist so the reader is not
          waiting on a round trip to learn the form is incomplete.
        */}
        {/*
          THE GRID IS NEVER EMPTY, so the "add at least one approver" branch that used to sit
          here is unreachable: `<InlineAddTable>` inserts a blank row the moment the count hits
          zero, including when the reader deletes the last one. A branch that can no longer
          render is drift, so it went rather than being left to read as a state.
        */}

        {/*
          IT COMPLAINS ONLY ONCE THE READER HAS FILLED ONE, and that qualifier is what keeps the
          form from OPENING in a failed state — the auto-inserted row is the resting state, so a
          bare `filled.length !== rows.length` put a red alert under an untouched form. That is
          the rule `useDeferredError` enforces for the shared inputs, reaching a message a modal
          renders itself.

          Nothing is lost by waiting: a lone blank row still fails `valid`, so Save stays
          disabled until an approver is chosen.
        */}
        {filled.length > 0 && filled.length !== rows.length ? (
          <p role="alert" className="text-xs text-red-700">
            Every row needs an approver. Remove the empty one, or choose an account for it.
          </p>
        ) : null}

        {duplicate ? (
          <p role="alert" className="text-xs text-red-700">
            One account appears twice. A chain that asks the same person to sign twice can never
            complete.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
